import { Check, Signal, Wifi, Battery, ArrowRight } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";

const WA_GREEN = "#25d366";
const WA_DARK = "#075e54";
const WA_BG = "#e5ddd5";

/**
 * Reusable WhatsApp-style phone mockup.
 *
 * Props:
 * - title: screen title shown in the chat header (e.g. "New Enquiry")
 * - businessName: bot/business display name (defaults to "Rekisa")
 * - messages: array of { from: 'bot' | 'user' | 'system', text: string, options?: string[] }
 * - statusBadge: optional small badge below the conversation (e.g. { label: 'Qualified', color: '#34a853' })
 * - className: outer wrapper classes
 * - size: 'sm' | 'md' | 'lg' (default 'md')
 */
export default function PhoneMockup({
  title = "WhatsApp",
  businessName = "Rekisa",
  messages = [],
  statusBadge,
  className = "",
  size = "md",
  stickBottom = false,
}) {
  const dims = {
    sm: { w: "w-[220px]", h: "h-[440px]" },
    md: { w: "w-[260px]", h: "h-[520px]" },
    lg: { w: "w-[280px]", h: "h-[560px]" },
  }[size];

  return (
    <div
      className={`relative ${dims.w} ${dims.h} rounded-[40px] bg-zinc-950 p-2.5 shadow-[0_30px_70px_-25px_rgba(0,0,0,0.4)] ${className}`}
    >
      {/* Notch */}
      <div className="absolute top-2.5 left-1/2 -translate-x-1/2 z-10 h-5 w-24 rounded-full bg-zinc-950" />

      <div className="relative h-full w-full rounded-[32px] overflow-hidden flex flex-col bg-white">
        {/* Status bar */}
        <div className="flex items-center justify-between px-5 pt-3 pb-1 text-[10px] font-semibold text-zinc-800">
          <span className="font-mono">9:41</span>
          <div className="flex items-center gap-1">
            <Signal className="h-3 w-3" />
            <Wifi className="h-3 w-3" />
            <Battery className="h-3 w-3" />
          </div>
        </div>

        {/* WhatsApp header */}
        <div
          className="flex items-center gap-2.5 px-3 py-2.5"
          style={{ background: WA_DARK }}
        >
          <div className="h-8 w-8 rounded-full bg-white/15 flex items-center justify-center text-white">
            <FaWhatsapp className="h-4 w-4" />
          </div>
          <div className="leading-tight">
            <div className="text-[12px] font-bold text-white">{businessName}</div>
            <div className="text-[10px] text-white/70">{title}</div>
          </div>
        </div>

        {/* Chat area */}
        <div
          className={`flex-1 overflow-hidden px-3 py-3 space-y-2 ${stickBottom ? "flex flex-col justify-end" : ""}`}
          style={{
            background: WA_BG,
            backgroundImage:
              "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='100' height='100'><path d='M20 20l60 60M80 20L20 80' stroke='%23d6cdc3' stroke-width='0.6' fill='none' opacity='0.45'/></svg>\")",
          }}
        >
          {messages.map((m, i) => (
            <ChatMessage key={`msg-${i}-${(m.text || "").slice(0, 24)}`} {...m} />
          ))}

          {statusBadge && (
            <div className="pt-2">
              <div className="rounded-lg bg-white/95 border border-zinc-200 px-3 py-2 text-[10px] text-zinc-800 leading-snug">
                <div className="flex items-center gap-1.5">
                  <span
                    className="h-2 w-2 rounded-full"
                    style={{ background: statusBadge.color }}
                  />
                  <span className="font-mono uppercase tracking-wider text-[9px] text-zinc-500">
                    Lead status
                  </span>
                </div>
                <div className="mt-0.5 font-bold text-zinc-900">{statusBadge.label}</div>
                {statusBadge.subtitle && (
                  <div className="mt-0.5 text-[10px] text-zinc-500">{statusBadge.subtitle}</div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Input bar */}
        <div className="px-3 py-2 border-t border-zinc-200 bg-white flex items-center gap-2">
          <div className="flex-1 h-7 rounded-full bg-zinc-100 px-3 flex items-center text-[10px] text-zinc-500">
            Type a message
          </div>
          <div
            className="h-7 w-7 rounded-full flex items-center justify-center text-white"
            style={{ background: WA_GREEN }}
          >
            <ArrowRight className="h-3.5 w-3.5" />
          </div>
        </div>
      </div>
    </div>
  );
}

function ChatMessage({ from, text, options, time = "9:41" }) {
  if (from === "system") {
    return (
      <div className="flex justify-center">
        <span className="rounded-md bg-white/80 px-2 py-0.5 text-[9px] text-zinc-600 font-mono">
          {text}
        </span>
      </div>
    );
  }
  const isUser = from === "user";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div
        className={`max-w-[82%] rounded-2xl px-3 py-2 text-[11px] leading-snug shadow-sm ${
          isUser
            ? "rounded-br-sm text-zinc-900"
            : "rounded-bl-sm text-zinc-900 bg-white"
        }`}
        style={isUser ? { background: "#dcf8c6" } : {}}
      >
        <div className="whitespace-pre-line">{text}</div>

        {options && options.length > 0 && (
          <div className="mt-1.5 flex flex-col gap-1 -mx-1">
            {options.map((opt) => (
              <div
                key={opt}
                className="rounded-md bg-white border border-zinc-200 px-2 py-1 text-[10px] text-[#075e54] font-semibold text-center"
              >
                {opt}
              </div>
            ))}
          </div>
        )}

        <div className="mt-1 flex items-center justify-end gap-1 text-[8px] text-zinc-500">
          <span>{time}</span>
          {isUser && <Check className="h-2.5 w-2.5 text-[#34b7f1]" strokeWidth={3} />}
        </div>
      </div>
    </div>
  );
}
